import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query/react'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useFetchInfluencerInfoQuery } from '@/api/influencer-api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolInfoQuery } from '@/api/workspace-api/kol-api'
import useKolInfoContext from '@/components/kol/hooks/use-kol-info-context'
import useIsQuotaBasedWorkspace from '@/hooks/use-is-quota-based-workspace'
import { usePermissions } from '@/hooks/use-permissions'
import type { Optional } from '@/types/optional'
import { KolSchemaModel } from '@/types/schema/kol-schema'

type UseKolInfo = (
  kolId?: string,
  kolDcIdHashed?: string,
) => {
  kolInfoForOpenGraph?: KolSchemaModel
  kolInfo?: KolSchemaModel
  isFetching: boolean
  isLocked: boolean
  isError: boolean
  error?: FetchBaseQueryError | SerializedError
}

const useKolInfo: UseKolInfo = (kolId, kolDcIdHashed) => {
  const kolInfoContext = useKolInfoContext()
  const { withKolPermission } = usePermissions()

  const {
    data: userStatus,
    isFetching: isFetchingUserStatus,
    isError: isErrorToFetchUserStatus,
    error: errorOfFetchUserStatus,
  } = useFetchUserStatusQuery(
    withKolPermission || kolInfoContext?.info ? skipToken : undefined,
  )
  const {
    data: kolInfoByAd,
    isFetching: isFetchingKolInfoByAd,
    isError: isErrorToFetchKolInfoForAd,
    error: errorOfFetchKolInfoForAd,
  } = useFetchKolInfoQuery(
    !withKolPermission && userStatus && kolId && !kolInfoContext?.info
      ? {
          radar_uuid: kolId,
          dc_id_hashed: kolDcIdHashed,
        }
      : skipToken,
  )

  const {
    data: kolInfoByKol,
    isFetching: isFetchingKolInfoByKol,
    isError: isErrorToFetchKolInfoForKol,
    error: errorOfFetchKolInfoForKol,
  } = useFetchInfluencerInfoQuery(
    withKolPermission && kolId && !kolInfoContext?.info ? kolId : skipToken,
  )

  const isQuotaBasedWorkspace = useIsQuotaBasedWorkspace(
    !!(withKolPermission || kolInfoContext?.info),
  )

  const isLocked = useMemo(() => {
    if (!isQuotaBasedWorkspace) {
      return false
    }

    if (!kolInfoByAd) {
      return true
    }

    // 當前時間是否超過購買時間的 180 天後
    return kolInfoByAd.snapshotAt
      ? dayjs().isAfter(dayjs(kolInfoByAd.snapshotAt).add(180, 'day'))
      : true
  }, [kolInfoByAd, isQuotaBasedWorkspace])

  const kolInfo = ((): Optional<KolSchemaModel> => {
    if (kolInfoContext?.info) {
      return kolInfoContext.info
    }

    if (withKolPermission) {
      return kolInfoByKol
    }

    return kolInfoByAd
  })()

  const isError = ((): ReturnType<UseKolInfo>['isError'] => {
    if (kolInfoContext?.info) {
      return false
    }

    if (withKolPermission) {
      return isErrorToFetchKolInfoForKol
    }

    return isErrorToFetchKolInfoForAd || isErrorToFetchUserStatus
  })()

  const error = ((): ReturnType<UseKolInfo>['error'] => {
    if (kolInfoContext?.info) {
      return undefined
    }

    if (withKolPermission) {
      return errorOfFetchKolInfoForKol
    }

    return isErrorToFetchUserStatus
      ? errorOfFetchUserStatus
      : errorOfFetchKolInfoForAd
  })()

  return {
    kolInfoForOpenGraph: kolInfoContext?.infoForOpenGraph,
    kolInfo,
    isFetching: withKolPermission
      ? isFetchingKolInfoByKol
      : isFetchingUserStatus || isFetchingKolInfoByAd,
    isLocked,
    isError,
    error,
  }
}

export default useKolInfo
